import { IconButton, type IconButtonProps, Tooltip } from '@mui/material';
import { forwardRef, type ReactNode } from 'react';
import { colors, radius, transitions } from '@/theme/tokens';

type AppIconButtonSize = 'sm' | 'md' | 'lg';
type AppIconButtonVariant = 'default' | 'ghost';

interface AppIconButtonProps extends Omit<IconButtonProps, 'size'> {
  size?: AppIconButtonSize;
  variant?: AppIconButtonVariant;
  tooltip?: string;
  children: ReactNode;
}

const sizeStyles = {
  sm: {
    width: '28px',
    height: '28px',
    '& svg': {
      fontSize: '16px',
    },
  },
  md: {
    width: '36px',
    height: '36px',
    '& svg': {
      fontSize: '20px',
    },
  },
  lg: {
    width: '44px',
    height: '44px',
    '& svg': {
      fontSize: '24px',
    },
  },
};

const variantStyles = {
  default: {
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    color: colors.textMuted,
    '&:hover': {
      backgroundColor: colors.surfaceRaised,
      borderColor: colors.textMuted,
      color: colors.textPrimary,
    },
  },
  ghost: {
    backgroundColor: 'transparent',
    border: 'none',
    color: colors.textMuted,
    '&:hover': {
      backgroundColor: colors.surfaceRaised,
      color: colors.textPrimary,
    },
  },
};

export const AppIconButton = forwardRef<HTMLButtonElement, AppIconButtonProps>(
  ({ size = 'md', variant = 'default', tooltip, children, sx, ...props }, ref) => {
    const button = (
      <IconButton
        ref={ref}
        sx={{
          ...sizeStyles[size],
          ...variantStyles[variant],
          borderRadius: radius.sm,
          transition: `all ${transitions.fast}`,
          '&:disabled': {
            color: colors.border,
          },
          ...sx,
        }}
        {...props}
      >
        {children}
      </IconButton>
    );

    if (tooltip) {
      return (
        <Tooltip title={tooltip} arrow>
          {button}
        </Tooltip>
      );
    }

    return button;
  }
);

AppIconButton.displayName = 'AppIconButton';
